import React, { useEffect, useRef, useState } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import FormField from '../ui/FormField';
import { useT } from '../../i18n/LocaleContext';

function formatCountdown(seconds) {
  const m = Math.floor(seconds / 60);
  const s = String(seconds % 60).padStart(2, '0');
  return `${m}:${s}`;
}

/**
 * Second step of every sensitive action (payouts, payout number change):
 * the backend e-mails a 6-digit code and hands back a challenge. The parent
 * owns the challenge; `onConfirm(code)` should throw on failure so the error
 * stays visible here, and `onResend()` should request a fresh challenge.
 */
export default function OtpModal({
  isOpen,
  onClose,
  title,
  intro,
  challenge,
  onConfirm,
  onResend,
  confirmLabel,
}) {
  const t = useT();
  const inputRef = useRef(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!isOpen) return undefined;
    setCode('');
    setError('');
    // Modal focuses its first control after children mount — defer so the
    // code input wins.
    const id = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(id);
  }, [isOpen, challenge?.challengeId]);

  useEffect(() => {
    if (!isOpen) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isOpen]);

  const expiresAt = challenge?.expiresAt ? new Date(challenge.expiresAt).getTime() : null;
  const remaining = expiresAt ? Math.max(0, Math.floor((expiresAt - now) / 1000)) : null;
  const expired = remaining === 0;

  const handleChange = (e) => {
    setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
    if (error) setError('');
  };

  const handleConfirm = async (e) => {
    e?.preventDefault?.();
    if (code.length !== 6) {
      setError(t('otp.invalid_code'));
      return;
    }
    setBusy(true);
    try {
      await onConfirm(code);
    } catch (err) {
      setError(err?.message || t('toast.generic_error'));
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setError('');
    try {
      await onResend();
      setCode('');
      inputRef.current?.focus();
    } catch (err) {
      setError(err?.message || t('toast.generic_error'));
    } finally {
      setResending(false);
    }
  };

  const locked = busy || resending;

  return (
    <Modal
      isOpen={isOpen}
      onClose={locked ? () => {} : onClose}
      closeOnBackdrop={false}
      size="sm"
      title={title || t('otp.title')}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={locked}>{t('common.cancel')}</Button>
          <Button
            variant="gold"
            onClick={handleConfirm}
            loading={busy}
            disabled={expired || resending || code.length !== 6}
          >
            {confirmLabel || t('common.confirm')}
          </Button>
        </>
      )}
    >
      <form onSubmit={handleConfirm} noValidate>
        {intro && <div className="otp-intro">{intro}</div>}
        <p className="modal-body-text otp-sent">
          {challenge?.sentTo ? t('otp.sent_to', { email: challenge.sentTo }) : t('otp.sent_generic')}
        </p>
        <FormField label={t('otp.code_label')} htmlFor="otp-code" error={error}>
          <input
            ref={inputRef}
            id="otp-code"
            className="form-control otp-input"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="\d{6}"
            maxLength={6}
            placeholder="••••••"
            value={code}
            onChange={handleChange}
            disabled={expired || resending}
            aria-invalid={Boolean(error)}
          />
        </FormField>
        <div className="otp-meta">
          <span className={expired ? 'otp-expiry is-expired' : 'otp-expiry'}>
            {remaining === null
              ? ''
              : expired
                ? t('otp.expired')
                : t('otp.expires_in', { time: formatCountdown(remaining) })}
          </span>
          <Button variant="link" size="sm" onClick={handleResend} loading={resending} disabled={busy}>
            {t('otp.resend')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
